import React, { useState } from 'react';
import { GetServerSideProps, NextPage } from 'next';
import Layout from '../../components/layout';
import useKnowledgebases from '../../hooks/use-knowledgebases';
import GenericContainer from '../../components/@container/generic-container';
import useKnowledgebaseCategories from '../../hooks/use-knowledgebase-categories';
import InputField from '../../components/input-field';
import DropdownField from '../../components/dropdown-field';
import Link from '../../components/link';
import KnowledgebaseGrid from '../../components/@knowledgebase/knowledgebase-grid';
import Button from '../../components/button';

interface Props {
  c: string;
}

const KnowledgeBasePage: NextPage<Props> = ({ c }) => {
  const [pageIndex, setPageIndex] = useState(1);
  const [search, setSearch] = useState('');
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState(c);
  const { data, loading, error } = useKnowledgebases(
    process.env.SITE!,
    title,
    category,
    pageIndex - 1,
    10
  );
  const {
    data: data1,
    loading: loading1,
    error: error1,
  } = useKnowledgebaseCategories(process.env.SITE!);

  if (loading || error) return null;
  if (!data) return null;
  if (loading1 || error1) return null;
  if (!data1) return null;

  const { knowledgebases } = data;
  const { knowledgebaseCategories } = data1;

  const onSearch = () => {
    setTitle(search);
  };

  const categoryOptions = () => {
    const options = [{ label: '- Choose One -', value: '' }];

    knowledgebaseCategories.forEach((c) =>
      options.push({ label: c.text, value: c.id })
    );

    return options;
  };

  return (
    <Layout title="Support">
      <GenericContainer
        title="Knowledge Base"
        breadcrumb={{ url: '/', text: 'Back to Home' }}
      >
        <div className="content">
          <p className="intro">
            The Knowledge Base and Help Request system is designed to service up
            answers and solutions to the most common questions, in the fastest
            and most intuitive way, without the need to submit a Help Request.
          </p>
          <p className="intro">
            Most questions can be answered quickly by entering keywords into the
            Search window. Our team has prepopulated the Knowledge Base with
            many possible error messages you might see, special tool information
            and policies that set the expectations for the support we provide.
          </p>
          <p className="intro">
            If a Help Request is still needed, please provide as much
            information as possible, to help us help you.
          </p>
          <p className="intro mb-2">
            Also, we encourage you to provide feedback and tell us if something
            was not found in the Knowledge or if we can add something to help
            you in the future.
          </p>
          <div className="group">
            <InputField
              id="search"
              label="Search"
              description="Enter a keyword or phrase to search for matching information"
              placeholder="Search Term"
              type="text"
              onChange={(e) => setSearch(e.target.value)}
              defaultValue={search}
              width="100%"
              marginBottom="0"
              padding="1rem 1rem"
            />
            <Button text="Search" onClick={onSearch} />
          </div>
          {!title && (
            <React.Fragment>
              <div className="divider" />
              <p className="category-title">VIDA</p>
              <div className="vida">
                <span>
                  <i className="fas fa-arrow-alt-circle-right" />
                </span>
                <div>
                  <Link href="/vida">
                    <a>
                      Click here for information for Independent VIDA
                      Subscription, Installation, Admin and Software Download
                      Information
                    </a>
                  </Link>
                </div>
              </div>
              <div className="vida">
                <span>
                  <i className="fas fa-arrow-alt-circle-right" />
                </span>
                <div>
                  <Link href="/vida/ir-workshop-instructional-library">
                    <a>
                      IR Workshop Instructional Library
                    </a>
                  </Link>
                </div>
              </div>
              <div className="divider" />
              <DropdownField
                id="category"
                label="Category"
                description="Use the drop down to select a category that matches your question"
                options={categoryOptions()}
                onChange={(e) => setCategory(e.target.value)}
                value={category}
                width="100%"
              />
            </React.Fragment>
          )}
          {(title || category) && (
            <React.Fragment>
              {knowledgebases.content.length > 0 ? (
                <KnowledgebaseGrid
                  knowledgebases={knowledgebases}
                  knowledgebaseCategories={knowledgebaseCategories}
                  pageIndex={pageIndex}
                  setPageIndex={setPageIndex}
                />
              ) : (
                <div>
                  <p>Could not find any knowledge bases.</p>
                  <Link href="/account/help/new">
                    <Button text="Create Help Request" />
                  </Link>
                </div>
              )}
              {knowledgebases.content.length > 0 &&
                knowledgebases.totalPages === pageIndex && (
                  <div style={{ textAlign: 'center' }}>
                    <p>
                      Didn't find the topic you were looking for? Click the
                      button below to create a help request.
                    </p>
                    <Link href="/account/help/new">
                      <Button text="Create Help Request" />
                    </Link>
                  </div>
                )}
            </React.Fragment>
          )}
        </div>
      </GenericContainer>
      {/* language=CSS */}
      <style jsx>{`
        p {
          font-family: 'Volvo Novum', sans-serif;
          font-size: 14px;
          margin: 0;
        }

        p.category-title {
          font-size: 16px;
        }

        p.intro {
          margin-bottom: 1rem;
        }

        p.intro.mb-2 {
          margin-bottom: 2rem;
        }

        a {
          font-family: 'Volvo Novum', sans-serif;
          font-size: 14px;
          color: #333;
          text-decoration: none;
          margin-bottom: 1rem;
        }

        a:hover {
          color: #707070;
        }

        .divider {
          margin-bottom: 2rem;
        }

        .vida {
          display: flex;
          gap: 1rem;
          margin-bottom: 0.5rem;
          padding: 1rem 0;
        }

        .vida:hover {
          background-color: #eee;
          cursor: pointer;
        }

        .vida span {
          display: block;
        }

        .group {
          display: flex;
          flex-direction: row;
          gap: 1rem;
          align-items: end;
          margin-bottom: 1rem;
        }
      `}</style>
    </Layout>
  );
};

export const getServerSideProps: GetServerSideProps = async ({ query }) => {
  const { c } = query;

  return {
    props: {
      c: c ?? '',
    },
  };
};

export default KnowledgeBasePage;
